import {
  AnimationMixer,
  Quaternion,
  Vector3,
  type KeyframeTrack,
  type Object3D,
} from 'three';
import * as SkeletonUtils from 'three/addons/utils/SkeletonUtils.js';
import type { BVH } from 'three/examples/jsm/loaders/BVHLoader.js';

import { resolvePrimaryPositionTrackName } from '@cartwhl/3d/bvh';

import type {
  KeyPoseSourcePosePayload,
  KeyPoseSkeletonSnapshot,
  KeyPoseTransformSnapshot,
} from './types';

interface CaptureKeyPoseSnapshotInput {
  scene: Object3D | null;
  bvh: BVH | null;
  frame: number;
  maxFrame?: number;
}

interface CreateKeyPoseSnapshotFromSourcePoseInput {
  scene: Object3D | null;
  bvh: BVH | null;
  sourcePose: KeyPoseSourcePosePayload;
}

interface CreateKeyPoseSnapshotFromSceneInput {
  scene: Object3D | null;
  bvh: BVH | null;
  frame: number;
}

interface CapturedKeyPoseSnapshot {
  frame: number;
  skeletonSnapshot: KeyPoseSkeletonSnapshot;
  sourcePose: KeyPoseSourcePosePayload;
}

export function captureKeyPoseSnapshotFromSourceScene({
  scene,
  bvh,
  frame,
  maxFrame,
}: CaptureKeyPoseSnapshotInput): CapturedKeyPoseSnapshot | null {
  if (!scene || !bvh) {
    return null;
  }

  const keyframeTimes = bvh.clip.tracks[0]?.times;
  if (!keyframeTimes || keyframeTimes.length === 0) {
    return null;
  }

  const requestedFrame = Math.max(Math.round(frame), 0);
  const timelineMaxFrame =
    typeof maxFrame === 'number' ? Math.max(Math.round(maxFrame), 0) : null;
  const frameInTimeline =
    timelineMaxFrame === null
      ? requestedFrame
      : Math.min(requestedFrame, timelineMaxFrame);
  const capturedFrame = Math.min(frameInTimeline, keyframeTimes.length - 1);
  const time = keyframeTimes[capturedFrame];
  if (!Number.isFinite(time)) {
    return null;
  }

  const sourcePose = createKeyPoseSourcePosePayloadFromBvh(bvh, capturedFrame);
  if (!sourcePose) {
    return null;
  }

  const snapshotScene = SkeletonUtils.clone(scene) as Object3D;
  poseSceneAtTime(snapshotScene, bvh, time);
  const skeletonSnapshot = createKeyPoseSkeletonSnapshot(
    snapshotScene,
    capturedFrame,
  );

  if (!hasKeyPoseSkeletonSnapshotTransforms(skeletonSnapshot)) {
    return null;
  }

  return {
    frame: capturedFrame,
    skeletonSnapshot,
    sourcePose,
  };
}

export function createKeyPoseSnapshotFromSourcePose({
  scene,
  bvh,
  sourcePose,
}: CreateKeyPoseSnapshotFromSourcePoseInput): CapturedKeyPoseSnapshot | null {
  if (!scene || !bvh) {
    return null;
  }

  const frame = Math.max(Math.round(sourcePose.frame), 0);
  const snapshotScene = SkeletonUtils.clone(scene) as Object3D;

  applyKeyPoseSourcePoseToScene(snapshotScene, bvh, sourcePose);
  const skeletonSnapshot = createKeyPoseSkeletonSnapshot(
    snapshotScene,
    frame,
  );

  if (!hasKeyPoseSkeletonSnapshotTransforms(skeletonSnapshot)) {
    return null;
  }

  return {
    frame,
    skeletonSnapshot,
    sourcePose: {
      ...sourcePose,
      frame,
    },
  };
}

export function createKeyPoseSnapshotFromScene({
  scene,
  bvh,
  frame,
}: CreateKeyPoseSnapshotFromSceneInput): CapturedKeyPoseSnapshot | null {
  if (!scene || !bvh) {
    return null;
  }

  const normalizedFrame = Math.max(Math.round(frame), 0);
  scene.updateMatrixWorld(true);

  const sourcePose = createKeyPoseSourcePosePayloadFromScene(
    scene,
    bvh,
    normalizedFrame,
  );
  if (!sourcePose) {
    return null;
  }

  const skeletonSnapshot = createKeyPoseSkeletonSnapshot(
    scene,
    normalizedFrame,
  );
  if (!hasKeyPoseSkeletonSnapshotTransforms(skeletonSnapshot)) {
    return null;
  }

  return {
    frame: normalizedFrame,
    skeletonSnapshot,
    sourcePose,
  };
}

export function applyKeyPoseSnapshotToScene(
  scene: Object3D,
  skeletonSnapshot: KeyPoseSkeletonSnapshot,
) {
  for (const [objectName, transform] of Object.entries(
    skeletonSnapshot.transformsByName,
  )) {
    const object = scene.getObjectByName(objectName);
    if (!object) {
      continue;
    }

    object.position.fromArray(transform.position);
    object.quaternion.fromArray(transform.quaternion);
    object.scale.fromArray(transform.scale);
    object.updateMatrix();
  }

  scene.updateMatrixWorld(true);
}

export function hasKeyPoseSkeletonSnapshotTransforms(
  skeletonSnapshot: KeyPoseSkeletonSnapshot | null | undefined,
) {
  return Boolean(
    skeletonSnapshot &&
      Object.keys(skeletonSnapshot.transformsByName).length > 0,
  );
}

function createKeyPoseSkeletonSnapshot(
  scene: Object3D,
  frame: number,
): KeyPoseSkeletonSnapshot {
  const transformsByName: Record<string, KeyPoseTransformSnapshot> = {};

  scene.traverse((object) => {
    if (!object.name) {
      return;
    }

    transformsByName[object.name] = {
      position: [object.position.x, object.position.y, object.position.z],
      quaternion: [
        object.quaternion.x,
        object.quaternion.y,
        object.quaternion.z,
        object.quaternion.w,
      ],
      scale: [object.scale.x, object.scale.y, object.scale.z],
    };
  });

  return {
    schemaVersion: 1,
    frame,
    transformsByName,
  };
}

function poseSceneAtTime(scene: Object3D, bvh: BVH, time: number) {
  const mixer = new AnimationMixer(scene);
  const action = mixer.clipAction(bvh.clip, scene);

  action.play();
  action.time = time;
  mixer.setTime(time);
  scene.updateMatrixWorld(true);
}

function applyKeyPoseSourcePoseToScene(
  scene: Object3D,
  bvh: BVH,
  sourcePose: KeyPoseSourcePosePayload,
) {
  const bones = bvh.skeleton.bones;

  bones.forEach((bone, index) => {
    const object = scene.getObjectByName(bone.name);
    const quaternion = axisAngleToQuaternion(sourcePose.localJointRot[index]);

    if (!object || !quaternion) {
      return;
    }

    object.quaternion.copy(quaternion);
    object.updateMatrix();
  });

  const rootPositionTrackName =
    resolvePrimaryPositionTrackName(bvh) ?? `${bones[0]?.name}.position`;
  const rootBoneName = getBoneNameFromPositionTrack(rootPositionTrackName);
  const rootObject = rootBoneName ? scene.getObjectByName(rootBoneName) : null;
  if (rootObject && isFiniteVector3Tuple(sourcePose.rootPosition)) {
    rootObject.position.set(
      sourcePose.rootPosition[0],
      sourcePose.rootPosition[1],
      sourcePose.rootPosition[2],
    );
    rootObject.updateMatrix();
  }

  scene.updateMatrixWorld(true);
}

export function createKeyPoseSourcePosePayloadFromBvh(
  bvh: BVH,
  frame: number,
): KeyPoseSourcePosePayload | null {
  const bones = bvh.skeleton.bones;
  if (!bones.length) {
    return null;
  }

  const tracksByName = new Map(
    bvh.clip.tracks.map((track) => [track.name, track] as const),
  );
  let capturedAnyRotation = false;
  const localJointRot = bones.map((bone) => {
    const quaternionTrack = tracksByName.get(`${bone.name}.quaternion`);
    const quaternionTuple = quaternionTrack
      ? getTrackTuple(quaternionTrack, frame, 4)
      : null;

    if (quaternionTuple) {
      capturedAnyRotation = true;
    }

    return quaternionTuple
      ? quaternionToAxisAngle(quaternionTuple)
      : [0, 0, 0];
  });
  const rootPositionTrackName =
    resolvePrimaryPositionTrackName(bvh) ?? `${bones[0].name}.position`;
  const rootPositionTrack = tracksByName.get(rootPositionTrackName);
  const rootPosition = rootPositionTrack
    ? getTrackTuple(rootPositionTrack, frame, 3)
    : null;

  if (!capturedAnyRotation || !rootPosition) {
    return null;
  }

  const roundedRootPosition = rootPosition.map(roundPoseNumber);

  return {
    frame,
    localJointRot,
    rootPosition: roundedRootPosition,
    smoothRoot2d: [roundedRootPosition[0] ?? 0, roundedRootPosition[2] ?? 0],
  };
}

function createKeyPoseSourcePosePayloadFromScene(
  scene: Object3D,
  bvh: BVH,
  frame: number,
): KeyPoseSourcePosePayload | null {
  const bones = bvh.skeleton.bones;
  if (!bones.length) {
    return null;
  }

  const localJointRot = bones.map((bone) => {
    const object = scene.getObjectByName(bone.name);

    return object ? quaternionToAxisAngle(object.quaternion) : [0, 0, 0];
  });
  const rootPositionTrackName =
    resolvePrimaryPositionTrackName(bvh) ?? `${bones[0].name}.position`;
  const rootBoneName = getBoneNameFromPositionTrack(rootPositionTrackName);
  const rootObject = rootBoneName ? scene.getObjectByName(rootBoneName) : null;
  if (!rootObject) {
    return null;
  }

  const rootPosition = [
    roundPoseNumber(rootObject.position.x),
    roundPoseNumber(rootObject.position.y),
    roundPoseNumber(rootObject.position.z),
  ];

  return {
    frame,
    localJointRot,
    rootPosition,
    smoothRoot2d: [rootPosition[0] ?? 0, rootPosition[2] ?? 0],
  };
}

function getTrackTuple(track: KeyframeTrack, frame: number, size: number) {
  const valueSize = track.getValueSize();
  if (valueSize < size || track.times.length === 0) {
    return null;
  }

  const clampedFrame = Math.min(
    Math.max(Math.round(frame), 0),
    track.times.length - 1,
  );
  const startIndex = clampedFrame * valueSize;
  const tuple = Array.from(track.values.slice(startIndex, startIndex + size));

  return tuple.every((value) => Number.isFinite(value)) ? tuple : null;
}

function quaternionToAxisAngle(
  quaternionTuple: readonly number[] | Quaternion,
) {
  const quaternion =
    quaternionTuple instanceof Quaternion
      ? quaternionTuple.clone().normalize()
      : new Quaternion(
          quaternionTuple[0] ?? 0,
          quaternionTuple[1] ?? 0,
          quaternionTuple[2] ?? 0,
          quaternionTuple[3] ?? 1,
        ).normalize();

  if (quaternion.w < 0) {
    quaternion.x *= -1;
    quaternion.y *= -1;
    quaternion.z *= -1;
    quaternion.w *= -1;
  }

  const w = Math.min(Math.max(quaternion.w, -1), 1);
  const angle = 2 * Math.acos(w);
  const scale = Math.sqrt(Math.max(0, 1 - w * w));
  if (scale < 1e-8 || angle < 1e-8) {
    return [0, 0, 0];
  }

  return [
    roundPoseNumber((quaternion.x / scale) * angle),
    roundPoseNumber((quaternion.y / scale) * angle),
    roundPoseNumber((quaternion.z / scale) * angle),
  ];
}

function axisAngleToQuaternion(axisAngle?: readonly number[]) {
  if (!isFiniteVector3Tuple(axisAngle)) {
    return null;
  }

  const angle = Math.hypot(axisAngle[0], axisAngle[1], axisAngle[2]);
  if (angle < 1e-8) {
    return new Quaternion();
  }

  return new Quaternion().setFromAxisAngle(
    new Vector3(
      axisAngle[0] / angle,
      axisAngle[1] / angle,
      axisAngle[2] / angle,
    ),
    angle,
  );
}

function getBoneNameFromPositionTrack(trackName: string | null) {
  const suffix = '.position';

  return trackName?.endsWith(suffix)
    ? trackName.slice(0, -suffix.length)
    : null;
}

function isFiniteVector3Tuple(value?: readonly number[]): value is number[] {
  return (
    Array.isArray(value) &&
    value.length >= 3 &&
    Number.isFinite(value[0]) &&
    Number.isFinite(value[1]) &&
    Number.isFinite(value[2])
  );
}

function roundPoseNumber(value: number) {
  return Math.round(value * 100000) / 100000;
}
